import type { EntityType } from '@/services/api';

const unitMultipliers = {
  bytes: 1,
  b: 1,
  kb: 1024,
  mb: 1024 ** 2,
  gb: 1024 ** 3,
  tb: 1024 ** 4,
};

export const formatFileSize = (bytes: number, locales = 'en') => {
  if (!bytes || bytes === 0) {
    return 'N/A';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const threshold = 1024;

  if (bytes < threshold) {
    return `${bytes} B`;
  }

  const i = Math.floor(Math.log(bytes) / Math.log(threshold));
  const value = bytes / threshold ** i;

  const formatter = new Intl.NumberFormat(locales, { maximumFractionDigits: 2 });

  return `${formatter.format(value)} ${units[i]}`;
};

export const joinAll = (arr: string | string[] | undefined, separator = ' | '): string => {
  if (!arr) {
    return '';
  }

  if (!Array.isArray(arr)) {
    return arr;
  }

  return arr.filter(Boolean).join(separator);
};

export const shortenText = (input: string, { minLength = 0, maxLength = 24 } = {}) => {
  if (!input) {
    return input;
  }

  if (input.length <= minLength) {
    return input; // Don't shorten if it's too short
  }

  return input.length > maxLength ? `${input.slice(0, maxLength)}...` : input;
};

export const parseContentSize = (value: string | number) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value; // Already in bytes
  }

  const regex = /^(\d+(?:\.\d+)?)(\s*)(bytes|b|kb|mb|gb|tb)$/i;

  if (typeof value !== 'string') {
    return null;
  }

  const match = value.trim().match(regex);
  if (!match) {
    return null;
  }

  if (!match[1] || !match[3]) {
    return null;
  }

  const number = parseFloat(match[1]);
  const unit = match[3].toLowerCase();

  return number * (unitMultipliers[unit as keyof typeof unitMultipliers] || 1);
};

export const getBasePathUrl = (path: string) => {
  const base = import.meta.env.BASE_URL || '/';
  const normalizedBase = base === '/' ? '' : base.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${normalizedBase}${normalizedPath}`;
};

export const getEntityUrl = (entity: EntityType) => {
  const { entityType } = entity;
  const id = encodeURIComponent(entity.id);
  switch (entityType) {
    case 'http://pcdm.org/models#Collection':
      return `/collection?id=${id}`;
    case 'http://pcdm.org/models#Object':
      return `/object?id=${id}`;
    case 'http://schema.org/Person':
      return `/person?id=${id}`;
    case 'http://schema.org/MediaObject':
      return `/file?id=${id}`;
    default:
      return `/entity?id=${id}`;
  }
};

// NOTE: This assumes the array is never empty from a type perspective
export const first = <T>(arr: T | T[]) => {
  if (!Array.isArray(arr)) {
    return arr;
  }

  return arr[0] as T;
};

export const formatDuration = (seconds: number) => {
  if (seconds < 0) {
    return '0s';
  }

  const units = [
    { label: 'd', value: 86400 },
    { label: 'h', value: 3600 },
    { label: 'm', value: 60 },
    { label: 's', value: 1 },
  ];

  const parts: string[] = [];
  let remaining = Math.floor(seconds);

  for (const unit of units) {
    if (remaining >= unit.value) {
      const count = Math.floor(remaining / unit.value);
      parts.push(`${count}${unit.label}`);
      remaining %= unit.value;
    }
  }

  return parts.length > 0 ? parts.join(' ') : '0s';
};

const exactMediaTypeIcons: Record<string, string> = {
  'text/csv': 'file-csv',
  'application/zip': 'file-zipper',
  'application/pdf': 'file-pdf',
  'application/msword': 'file-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
  'application/x-ipynb+json': 'clipboard',
  'application/mp4': 'file-video',
  'application/mxf': 'file-video',
  'application/json': 'file-code',
};

const mediaTypeIcon = (mediaType: string) => {
  const exact = exactMediaTypeIcons[mediaType];
  if (exact) {
    return exact;
  }

  if (mediaType.startsWith('video')) {
    return 'file-video';
  }

  if (mediaType.startsWith('audio')) {
    return 'file-audio';
  }

  if (mediaType.startsWith('image')) {
    return 'file-image';
  }

  if (mediaType.startsWith('text')) {
    return 'file-lines';
  }

  if (mediaType.includes('xml')) {
    return 'file-code';
  }

  console.log(`Need mediaType icon for ${mediaType}`);

  return 'file';
};

export const groupMediaTypesByIcon = (mediaTypes: string[]) => {
  const groups = new Map<string, string[]>();
  for (const mediaType of new Set(mediaTypes)) {
    const icon = mediaTypeIcon(mediaType);
    const group = groups.get(icon);
    if (group) {
      group.push(mediaType);
    } else {
      groups.set(icon, [mediaType]);
    }
  }

  return [...groups].map(([icon, types]) => ({ icon, mediaTypes: types }));
};
